/**
 * Fantasy-only entrance animation: the wrapped content unrolls open like a
 * parchment scroll, with two gilded rods that slide apart from the center
 * and fade once the reveal completes. Only meant to wrap a page's main
 * content tile (e.g. BlogReader's article card) — mount it once per page
 * view, not per re-render, since it only plays on mount.
 */

import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

interface ParchmentRevealProps {
  children: React.ReactNode;
  className?: string;
  /** Set true outside the Fantasy variant to render children unwrapped. */
  disabled?: boolean;
}

const ParchmentReveal: React.FC<ParchmentRevealProps> = ({ children, className = '', disabled = false }) => {
  const prefersReducedMotion = useReducedMotion();
  const [rodsVisible, setRodsVisible] = useState(!prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion || disabled) return;
    const timer = setTimeout(() => setRodsVisible(false), 750);
    return () => clearTimeout(timer);
  }, [prefersReducedMotion, disabled]);

  if (disabled || prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      <motion.div
        initial={{ clipPath: 'inset(50% 0% 50% 0%)' }}
        animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>

      {rodsVisible && (
        <>
          <motion.div
            className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 pointer-events-none z-20 flex items-center"
            initial={{ opacity: 1, y: 0 }}
            animate={{ y: '-50%', opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="h-2 w-2 rounded-full bg-[#b8860b] shadow-md shrink-0" />
            <span className="flex-1 h-1.5 bg-gradient-to-r from-[#b8860b] via-[#d4af37] to-[#b8860b] shadow-md" />
            <span className="h-2 w-2 rounded-full bg-[#b8860b] shadow-md shrink-0" />
          </motion.div>
          <motion.div
            className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 pointer-events-none z-20 flex items-center"
            initial={{ opacity: 1, y: 0 }}
            animate={{ y: '50%', opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="h-2 w-2 rounded-full bg-[#b8860b] shadow-md shrink-0" />
            <span className="flex-1 h-1.5 bg-gradient-to-r from-[#b8860b] via-[#d4af37] to-[#b8860b] shadow-md" />
            <span className="h-2 w-2 rounded-full bg-[#b8860b] shadow-md shrink-0" />
          </motion.div>
        </>
      )}
    </div>
  );
};

export default ParchmentReveal;
